import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ClipboardPaste, FileText, Search, Upload } from 'lucide-react'
import { api } from '../lib/api'
import type { DocumentResume } from '../lib/types'
import { Carte, Chargement, EtatVide, Pastille } from '../composants/base'
import VisionneuseDocument from '../composants/VisionneuseDocument'
import AjoutDocument from '../composants/AjoutDocument'
import * as f from '../lib/format'
import { ORIGINES_DOCUMENT, TYPES_DOCUMENT } from '@doc/shared'

export default function Documents() {
  const [saisie, setSaisie] = useState('')
  const [terme, setTerme] = useState('')
  const [type, setType] = useState('')
  const [ouvert, setOuvert] = useState<string | null>(null)

  const [ajout, setAjout] = useState<null | 'fichier' | 'texte'>(null)

  useEffect(() => {
    const minuteur = setTimeout(() => setTerme(saisie.trim()), 220)
    return () => clearTimeout(minuteur)
  }, [saisie])

  const { data, isLoading } = useQuery({
    queryKey: ['documents', terme, type],
    queryFn: () =>
      api<{ documents: DocumentResume[] }>(
        `/api/documents?limite=200${terme ? `&q=${encodeURIComponent(terme)}` : ''}${
          type ? `&type=${type}` : ''
        }`,
      ),
  })

  return (
    <div className="mx-auto max-w-[1180px] pt-1">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-[20px] font-semibold tracking-tight">Documents</h1>
        <div className="flex gap-2">
          <button className="bouton-doux" onClick={() => setAjout('texte')}>
            <ClipboardPaste className="h-4 w-4" />
            Coller un texte
          </button>
          <button className="bouton-primaire" onClick={() => setAjout('fichier')}>
            <Upload className="h-4 w-4" />
            Ajouter un document
          </button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative max-w-[460px] flex-1">
          <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-brume" />
          <input
            className="champ pl-9"
            placeholder="Titre ou contenu du document"
            value={saisie}
            onChange={(e) => setSaisie(e.target.value)}
          />
        </div>
        <select className="champ w-[260px]" value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">Tous les types</option>
          {Object.entries(TYPES_DOCUMENT).map(([cle, libelle]) => (
            <option key={cle} value={cle}>
              {libelle}
            </option>
          ))}
        </select>
      </div>

      <Carte>
        {isLoading ? (
          <Chargement />
        ) : !data || data.documents.length === 0 ? (
          <EtatVide
            texte="Aucun document. Les documents se déposent depuis le dossier d’un patient."
            icone={<FileText className="h-8 w-8" />}
          />
        ) : (
          <ul className="px-2 py-2">
            {data.documents.map((d) => (
              <li key={d.id} className="flex items-center gap-3 rounded-lg px-3 py-2.5 hover:bg-bleu-50">
                <button className="min-w-0 flex-1 text-left" onClick={() => setOuvert(d.id)}>
                  <span className="flex items-center gap-2">
                    <FileText className="h-4 w-4 shrink-0 text-bleu-400" />
                    <span className="truncate text-[14px]">{d.titre}</span>
                  </span>
                  <span className="mt-0.5 block truncate pl-6 text-[12.5px] text-brume">
                    {TYPES_DOCUMENT[d.type as keyof typeof TYPES_DOCUMENT]} ·{' '}
                    {d.dateDocument ? f.dateCourte(d.dateDocument) : 'date inconnue'} · déposé{' '}
                    {f.ilYA(d.dateAjout)}
                  </span>
                </button>

                <Pastille ton="bleu">
                  {ORIGINES_DOCUMENT[d.origine as keyof typeof ORIGINES_DOCUMENT]}
                </Pastille>

                {d.patient && (
                  <Link
                    to={`/patients/${d.patient.id}`}
                    className="w-[190px] shrink-0 truncate text-right text-[12.5px] text-bleu-600 hover:underline"
                  >
                    {d.patient.affichage}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        )}
      </Carte>

      {ouvert && <VisionneuseDocument documentId={ouvert} fermer={() => setOuvert(null)} />}

      {ajout && <AjoutDocument quoi={ajout} fermer={() => setAjout(null)} />}
    </div>
  )
}
